import { useState, useRef, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Check, SkipForward } from 'lucide-react';

// Output dimensions of the final cropped photo.
const OUTPUT_W = 1200;
const OUTPUT_H = 900; // 4:3 — works well for portraits and landscape
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;

/**
 * Allows the user to pan + zoom + rotate each uploaded image inside a fixed 4:3 frame.
 * Returns a `File` (JPEG) for every selected image.
 *
 * Props:
 *  - files: File[]  — the original images selected by the user
 *  - onDone(results: File[])  — called when the user has processed every image
 *  - onCancel()  — close without uploading
 */
export default function ImageCropper({ files, onDone, onCancel }) {
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgEl, setImgEl] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const containerRef = useRef(null);
  const draggingRef = useRef(null);

  const current = files[index];

  useEffect(() => {
    if (!current) return;
    setZoom(1); setRotation(0); setOffset({ x: 0, y: 0 });
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = URL.createObjectURL(current);
    return () => URL.revokeObjectURL(img.src);
  }, [current]);

  const containerSize = 380;
  const previewRatio = OUTPUT_W / OUTPUT_H;
  const previewW = containerSize;
  const previewH = Math.round(containerSize / previewRatio);

  // Compute base scale: image must cover the frame at zoom=1
  const baseScale = imgEl && naturalSize.w > 0 && naturalSize.h > 0
    ? Math.max(previewW / naturalSize.w, previewH / naturalSize.h)
    : 1;

  const startDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    draggingRef.current = { startX: clientX, startY: clientY, originX: offset.x, originY: offset.y };
  };
  const moveDrag = (e) => {
    if (!draggingRef.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setOffset({
      x: draggingRef.current.originX + (clientX - draggingRef.current.startX),
      y: draggingRef.current.originY + (clientY - draggingRef.current.startY),
    });
  };
  const endDrag = () => { draggingRef.current = null; };

  const renderCanvas = async () => {
    if (!imgEl) return null;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_W;
    canvas.height = OUTPUT_H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_W, OUTPUT_H);
    // The scale needs to map preview coordinates to output coordinates
    const scaleFactor = OUTPUT_W / previewW;
    const totalScale = baseScale * zoom * scaleFactor;
    ctx.translate(OUTPUT_W / 2 + offset.x * scaleFactor, OUTPUT_H / 2 + offset.y * scaleFactor);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(totalScale, totalScale);
    ctx.drawImage(imgEl, -naturalSize.w / 2, -naturalSize.h / 2);
    return await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', 0.88));
  };

  const handleConfirm = async () => {
    const blob = await renderCanvas();
    if (!blob) return;
    const newFile = new File([blob], (current.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    const next = [...results, newFile];
    if (index + 1 >= files.length) {
      onDone(next);
    } else {
      setResults(next);
      setIndex(index + 1);
    }
  };

  const handleSkip = () => {
    // Upload original (just compressed by browser util) — keep behaviour
    const next = [...results, current];
    if (index + 1 >= files.length) {
      onDone(next);
    } else {
      setResults(next);
      setIndex(index + 1);
    }
  };

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="image-cropper">
      <div className="bg-card rounded-2xl shadow-2xl max-w-md w-[95%] m-4 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-base">Ajustar foto ({index + 1}/{files.length})</h3>
          <button onClick={onCancel} title="Cancelar tudo"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-muted-foreground">Arrasta para reposicionar e usa os botões para zoom / rotação. A área visível será guardada.</p>
        <div className="flex justify-center">
          <div ref={containerRef}
            onMouseDown={startDrag} onMouseMove={moveDrag} onMouseUp={endDrag} onMouseLeave={endDrag}
            onTouchStart={startDrag} onTouchMove={moveDrag} onTouchEnd={endDrag}
            className="relative bg-black overflow-hidden rounded-lg cursor-move select-none touch-none"
            style={{ width: previewW, height: previewH }}>
            {imgEl && (
              <img src={imgEl.src} alt="" draggable={false}
                style={{
                  position: 'absolute',
                  left: '50%', top: '50%',
                  width: naturalSize.w * baseScale,
                  height: naturalSize.h * baseScale,
                  transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom})`,
                  transformOrigin: 'center',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }} />
            )}
            <div className="pointer-events-none absolute inset-0 border-2 border-white/30" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 text-xs">
          <button onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - 0.15))} title="Diminuir zoom"
            className="p-2 rounded-full bg-muted hover:bg-border" data-testid="crop-zoom-out">
            <ZoomOut className="w-4 h-4" />
          </button>
          <input type="range" min={MIN_ZOOM} max={MAX_ZOOM} step={0.05}
            value={zoom} onChange={e => setZoom(parseFloat(e.target.value))}
            className="flex-1 max-w-[160px]" />
          <button onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + 0.15))} title="Aumentar zoom"
            className="p-2 rounded-full bg-muted hover:bg-border" data-testid="crop-zoom-in">
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={() => setRotation(r => (r + 90) % 360)} title="Rodar 90º"
            className="p-2 rounded-full bg-muted hover:bg-border" data-testid="crop-rotate">
            <RotateCw className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <button onClick={handleSkip} title="Usar foto original"
            className="px-3 py-2 rounded-full text-xs bg-muted text-foreground hover:bg-border flex items-center gap-1" data-testid="crop-skip">
            <SkipForward className="w-3.5 h-3.5" /> Usar original
          </button>
          <button onClick={handleConfirm} data-testid="crop-confirm"
            className="px-4 py-2 rounded-full text-sm font-medium bg-primary text-primary-foreground flex items-center gap-1">
            <Check className="w-4 h-4" /> {index + 1 < files.length ? 'Próxima' : 'Concluir'}
          </button>
        </div>
      </div>
    </div>
  );
}
